/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { readAndParse } from "@fluidframework/driver-utils/internal";
import { addBlobToSummary } from "@fluidframework/runtime-utils/internal";
import { DataCorruptionError, tagCodeArtifacts, } from "@fluidframework/telemetry-utils/internal";
import { ChannelDeltaConnection } from "./channelDeltaConnection.js";
import { ChannelStorageService } from "./channelStorageService.js";
export const attributesBlobKey = ".attributes";
export function createChannelServiceEndpoints(connected, submitFn, dirtyFn, isAttachedAndVisible, storageService, logger, tree, extraBlobs) {
    const deltaConnection = new ChannelDeltaConnection(connected, (message, localOpMetadata) => submitFn(message, localOpMetadata), dirtyFn, isAttachedAndVisible);
    const objectStorage = new ChannelStorageService(tree, storageService, logger, extraBlobs);
    return {
        deltaConnection,
        objectStorage,
    };
}
/** Used to get the channel's summary for the DDS or DataStore attach op */
export function summarizeChannel(channel, fullTree = false, trackState = false, telemetryContext) {
    const summarizeResult = channel.getAttachSummary(fullTree, trackState, telemetryContext);
    // Add the channel attributes to the returned result.
    addBlobToSummary(summarizeResult, attributesBlobKey, JSON.stringify(channel.attributes));
    return summarizeResult;
}
export async function summarizeChannelAsync(channel, fullTree = false, trackState = false, telemetryContext, incrementalSummaryContext) {
    const summarizeResult = await channel.summarize(fullTree, trackState, telemetryContext, incrementalSummaryContext);
    // Add the channel attributes to the returned result.
    addBlobToSummary(summarizeResult, attributesBlobKey, JSON.stringify(channel.attributes));
    return summarizeResult;
}
export async function loadChannelFactoryAndAttributes(dataStoreContext, services, channelId, registry, attachMessageType) {
    let attributes;
    if (await services.objectStorage.contains(attributesBlobKey)) {
        attributes = await readAndParse(services.objectStorage, attributesBlobKey);
    }
    // This is a backward compatibility case where the attach message doesn't include attributes. They must
    // include attach message type.
    // Since old attach messages will not have attributes, we need to keep this as long as we support old attach
    // messages.
    const channelFactoryType = attributes ? attributes.type : attachMessageType;
    if (channelFactoryType === undefined) {
        throw new DataCorruptionError("channelTypeNotAvailable", tagCodeArtifacts({
            channelId,
            dataStoreId: dataStoreContext.id,
            dataStorePackagePath: dataStoreContext.packagePath.join("/"),
            channelFactoryType,
        }));
    }
    const factory = registry.get(channelFactoryType);
    if (factory === undefined) {
        throw new DataCorruptionError("channelFactoryNotRegisteredForGivenType", tagCodeArtifacts({
            channelId,
            dataStoreId: dataStoreContext.id,
            dataStorePackagePath: dataStoreContext.packagePath.join("/"),
            channelFactoryType,
        }));
    }
    // This is a backward compatibility case where the attach message doesn't include attributes. Get the attributes
    // from the factory.
    attributes = attributes ?? factory.attributes;
    return { factory, attributes };
}
export async function loadChannel(dataStoreRuntime, attributes, factory, services, logger, channelId) {
    // Compare snapshot version to collaborative object version
    if (attributes.snapshotFormatVersion !== undefined &&
        attributes.snapshotFormatVersion !== factory.attributes.snapshotFormatVersion) {
        logger.sendTelemetryEvent({
            eventName: "ChannelAttributesVersionMismatch",
            ...tagCodeArtifacts({
                channelType: attributes.type,
                channelSnapshotVersion: `${attributes.snapshotFormatVersion}@${attributes.packageVersion}`,
                channelCodeVersion: `${factory.attributes.snapshotFormatVersion}@${factory.attributes.packageVersion}`,
            }),
        });
    }
    return factory.load(dataStoreRuntime, channelId, services, attributes);
}
//# sourceMappingURL=channelContext.js.map